import React, { useEffect, useState, useRef, useCallback } from 'react';
import clsx from 'clsx';
import { injectedComponent, Text, useText } from '@base/core';
import { getRussianPluralVariant } from '@base/utils/russianPluralization';
import { useLocaleSwitchApp } from '../hooks/i18n';
import { useSessionStorage } from '../hooks/useSessionStorage';
import { useTimers } from '../hooks/useTimers';
import { CodeInputs } from './components/CodeInputs';
import { formatTime } from '../utils/time';
import { SESSION_KEY_ERROR_CODES } from '../constants/sessionKey';

const {
  PASSWORD_CHANGE_DATA_NOT_FOUND,
  SAVED_AUTH_DATA_NOT_FOUND,
  MEMBER_ALREADY_ACTIVE,
  INVALID_LOGIN_PASSWORD,
} = SESSION_KEY_ERROR_CODES;

export default injectedComponent(
  {
    name: 'SessionKeyDialog',
    services: ['api', 'dialog', 'notifier', 'access'],
    styles: ['dialog', 'targetDialog', 'button', 'self'],
  },
  function SessionKeyDialog({
                              services: { api, dialog, notifier, access },
                              styles: {
                                dialog: dialogStyles,
                                targetDialog: targetDialogStyles,
                                button: buttonStyles,
                                self: selfStyles,
                              },
                            }) {
    const [code, setCode] = useState(['', '', '', '', '', '']);
    const [loading, setLoading] = useState(false);
    const [resending, setResending] = useState(false);
    const [error, setError] = useState('');
    const [attemptsLeft, setAttemptsLeft] = useState(null);
    const [showBlockMessage, setShowBlockMessage] = useState(false);
    const [disableSendingSessionKey, setDisableSendingSessionKey] = useState(false);
    const [isHovering, setIsHovering] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [isResendLimit, setIsResendLimit] = useState(false);
    const [displayRemainingTime, setDisplayRemainingTime] = useState(0);

    const text = useText();
    const switchApp = useLocaleSwitchApp();
    const inputRefs = useRef([]);
    const sessionDataRef = useRef(null);
    const signupCompletedRef = useRef(false);
    const preserveCurrentErrorRef = useRef(false);

    const [emailFromParam, setEmailFromParam] = useState(null);
    const [sessionAction, setSessionAction] = useState(() => sessionDataRef.current?.action || null);

    const emailForStorage = emailFromParam || sessionDataRef.current?.email;
    const storage = useSessionStorage(emailForStorage);

    const {
      resendTimer, setResendTimer,
      longResendTimer, setLongResendTimer
    } = useTimers(emailForStorage);


    const persistState = useCallback((key, value) => {
      if (!storage) return;
      
      if (signupCompletedRef.current) return;
      storage.set(key, value);
    }, [storage]);

    const getErrorMessageWithAttempts = useCallback((attempts) => {
      if (attempts === null || attempts === undefined) {
        return text('dialog.session_key.error_invalid');
      }
      const pluralVariant = getRussianPluralVariant(attempts);
      const attemptsText = text(`dialog.session_key.attempts_remaining${pluralVariant}`, { count: attempts });
      return `${text('dialog.session_key.error_invalid')}. \n ${attemptsText}`;
    }, [text]);

    const getRemainingTime = useCallback(() => {
      if (!longResendTimer || longResendTimer <= 0) return 0;
      const nowSec = Math.floor(Date.now() / 1000);
      return Math.max(0, longResendTimer - nowSec);
    }, [longResendTimer]);

    useEffect(() => {
      if (!longResendTimer || longResendTimer <= 0) {
        setDisplayRemainingTime(0);
        return;
      }

      const updateRemainingTime = () => {
        const remaining = getRemainingTime();
        setDisplayRemainingTime(remaining);
      };

      updateRemainingTime();

      const interval = setInterval(() => {
        updateRemainingTime();
      }, 1000);

      return () => clearInterval(interval);
    }, [longResendTimer, getRemainingTime]);

    const hasValidResendLimitError = useCallback(() => {
      if (!storage) return false;
      const errorData = storage.get('error');
      if (!errorData || errorData.type !== 'resend_limit') return false;
      
      if (errorData.expiresAt) {
        const nowSec = Math.floor(Date.now() / 1000);
        return errorData.expiresAt > nowSec;
      }
      
      const remainingTime = getRemainingTime();
      return remainingTime > 0;
    }, [storage, getRemainingTime]);

    const processResponseError = useCallback((response) => {
      setLoading(false);

        if (!response) {
          setErrorWithReset(text('dialog.session_key.error_invalid'));
          return;
        }

      if (response.result === 'error' || response.checkResult === false) {
        const message = response.message;

        if (message?.includes('Member already exist') || message === 'Incorrect session key') {
          const newAttemptsLeft = response.attemptsLeft ?? attemptsLeft;
          const errorMessage = getErrorMessageWithAttempts(newAttemptsLeft);

            setAttemptsLeft(newAttemptsLeft);
            setErrorWithReset(errorMessage);

          if (storage) {
            storage.set('attemptsLeft', { attempts: newAttemptsLeft });
            if (!hasValidResendLimitError()) {
              storage.set('error', { error: errorMessage, type: 'attempts' });
            }
          }
        }
        else if (message === 'Session key expired') {
          setShowBlockMessage(true);
          setDisableSendingSessionKey(true);
          
          const errorExpiredKey = sessionDataRef.current?.action === 'login'
            ? 'dialog.session_key.error_session_key_expired__login'
            : sessionDataRef.current?.action === 'change_password'
            ? 'dialog.session_key.error_session_key_expired__change_password'
            : 'dialog.session_key.error_session_key_expired';
          
          const errorExpiredText = text(errorExpiredKey);
          setErrorWithReset(errorExpiredText);

          storage.set('blockMessage', { show: true });
          if (!hasValidResendLimitError()) {
            storage.set('error', { error: errorExpiredText, type: 'expired' });
          }

          if (response.attemptsLeft !== undefined) {
            setAttemptsLeft(response.attemptsLeft);
            storage.set('attemptsLeft', { attempts: response.attemptsLeft });
          }
        }
        else if (message === 'Number of attempts exceeded') {
          setShowBlockMessage(true);
          setDisableSendingSessionKey(true);
          
          const longBlockKey = sessionDataRef.current?.action === 'login'
            ? 'dialog.session_key.long_block_message__login'
            : sessionDataRef.current?.action === 'change_password'
            ? 'dialog.session_key.long_block_message__change_password'
            : 'dialog.session_key.long_block_message';
          
          const longBlockText = text(longBlockKey);
          setErrorWithReset(longBlockText);

          storage.set('blockMessage', { show: true });
          if (!hasValidResendLimitError()) {
            storage.set('error', { error: longBlockText, type: 'blocked' });
          }

          if (response.attemptsLeft !== undefined) {
            setAttemptsLeft(response.attemptsLeft);
            storage.set('attemptsLeft', { attempts: response.attemptsLeft });
          }
        }
        else if (message === 'Session key request limit exceeded') {
          setIsResendLimit(true);
          storage.remove('blockMessage');
          setErrorWithReset(text('dialog.session_key.long_resend_limit_exceeded'));
          
          const errorData = {
            error: text('dialog.session_key.long_resend_limit_exceeded'),
            type: 'resend_limit'
          };
          
          let expiresAt = null;
          
          if (response.blockUntil || response.sessionKeyTTL) {
            const blockUntil = response.blockUntil || (response.sessionKeyTTL ? Math.floor(Date.now() / 1000) + response.sessionKeyTTL : null);
            if (blockUntil) {
              expiresAt = blockUntil;
              if (!longResendTimer || longResendTimer !== expiresAt) {
                setLongResendTimer(expiresAt);
                persistState('longResendTimer', { endTime: expiresAt });
              }
            }
          }
          
          if (!expiresAt && longResendTimer && longResendTimer > 0) {
            expiresAt = longResendTimer;
          }
          
          if (!expiresAt) {
            const savedLongResendTimer = storage?.get('longResendTimer');
            if (savedLongResendTimer && savedLongResendTimer.endTime) {
              expiresAt = savedLongResendTimer.endTime;
            }
          }
          
          if (expiresAt) {
            errorData.expiresAt = expiresAt;
          }
          
          storage.set('error', errorData);
        }
        else if (message === PASSWORD_CHANGE_DATA_NOT_FOUND) {
          setErrorWithReset(text('dialog.session_key.error_password_change_data_not_found'));
          if (!hasValidResendLimitError()) {
            storage.set('error', {
              error: text('dialog.session_key.error_password_change_data_not_found'),
              type: 'password_change_data_not_found'
            });
          }
        }
        else if (message === SAVED_AUTH_DATA_NOT_FOUND) {
          setErrorWithReset(text('dialog.session_key.error_saved_auth_data_not_found'));
          if (!hasValidResendLimitError()) {
            storage.set('error', {
              error: text('dialog.session_key.error_saved_auth_data_not_found'),
              type: 'saved_auth_data_not_found'
            });
          }
        }
        else if (message === MEMBER_ALREADY_ACTIVE) {
          setErrorWithReset(text('dialog.session_key.error_member_already_active'));
          if (!hasValidResendLimitError()) {
            storage.set('error', {
              error: text('dialog.session_key.error_member_already_active'),
              type: 'member_already_active'
            });
          }
        }
        else if (message === INVALID_LOGIN_PASSWORD) {
          const authErrorText = text('dialog.session_key.error_invalid_login_password');
          setErrorWithReset(authErrorText);
          if (!hasValidResendLimitError()) {
            storage.set('error', {
              error: authErrorText,
              type: 'invalid_login_password'
            });
          }
        }
        else if (response.urlError) {
          setErrorWithReset(message);
          if (!hasValidResendLimitError()) {
            storage.set('error', { 
              error: message, 
              type: 'url_error'
            });
          }
        }
          else {
            setErrorWithReset(message || text('dialog.session_key.error_invalid'));
            if (!hasValidResendLimitError()) {
              storage.set('error', { error: message || text('dialog.session_key.error_invalid'), type: 'invalid' });
            }
          }
      }
    }, [attemptsLeft, getErrorMessageWithAttempts, storage, text, persistState, longResendTimer, hasValidResendLimitError]);

    const handleCodeChange = useCallback((index, value) => {
      setCode(prevCode => {
        const newCode = [...prevCode];
        newCode[index] = value;
        return newCode;
      });
    }, []);

    const handleInputFocus = useCallback((index) => {
      setIsFocused(true);
      if (error && loading) {
        setLoading(false);
      }
    }, [error, loading]);

    const handleMouseEnter = () => {
      if (!loading && !resending && !disableSendingSessionKey) {
        setIsHovering(true);
      }
    };

    const handleMouseLeave = () => {
      setIsHovering(false);
    };

    const handleInputBlur = () => {
    };

    const setErrorWithReset = (errorMessage) => {
      setIsFocused(false);
      setIsHovering(false);
      setError(errorMessage);
      preserveCurrentErrorRef.current = true;
    };

    const clearErrorIfAllowed = useCallback(() => {
      if (!storage) return;
      
      const errorData = storage.get('error');
      if (!errorData) {
        if (error) {
          setError('');
          preserveCurrentErrorRef.current = false;
        }
        return;
      }
      
      const isResendLimitValid = errorData.type === 'resend_limit' && 
        errorData.expiresAt && errorData.expiresAt > Math.floor(Date.now() / 1000);
      
      if (!isResendLimitValid) {
        setError('');
        preserveCurrentErrorRef.current = false;
        storage.remove('error');
      }
    }, [storage, error]);

    useEffect(() => {
      if (error) {
        setIsFocused(false);
        setIsHovering(false);
      }
    }, [error]);


    const handleSubmit = async () => {
      const sessionKey = code.join('');
      notifier.closeAll();

        if (!storage) {
          setErrorWithReset(text('dialog.session_key.error_network'));
          return;
        }

        if (sessionKey.length !== 6) {
          setErrorWithReset(text('dialog.session_key.error_incomplete'));
          if (!hasValidResendLimitError()) {
            storage.set('error', { error: text('dialog.session_key.error_incomplete'), type: 'incomplete' });
          }
          return;
        }

        if (!sessionDataRef.current?.memberId) {
          setErrorWithReset(text('dialog.session_key.error_network'));
          if (!hasValidResendLimitError()) {
            storage.set('error', { error: text('dialog.session_key.error_network'), type: 'network' });
          }
          return;
        }

      setLoading(true);
      try {
        const response = await api.sessionKeyConfirm({
          payload: {
            memberId: sessionDataRef.current.memberId,
            sessionKey: sessionKey,
            action: sessionDataRef.current.action,
          }
        });

        if (response && response.checkResult === true) {
          setError('');
          preserveCurrentErrorRef.current = false;
          signupCompletedRef.current = true;
          storage.clearAll();
          if ((sessionDataRef.current.action === 'signup' || sessionDataRef.current.action === 'signuptg') && response.data?.token) {
            notifier.closeAll();
            access.login(response.data.token, {
              memberId: response.memberId,
              isLogged: true,
              fullMemberAccess: response.active,
              viewCabinetMemberAccess: false
            });
            if (sessionDataRef.current.action === 'signuptg') {
              let data = await api.botJoin({
                payload: {
                  userToken: localStorage.authToken,
                },
                options: {
                  authToken: localStorage.authToken,
                },
              });
              if (data?.data) {
                window.open(data?.data, '_blank').focus();
              } else {
                console.error(data);
              }
            }
          } else if (sessionDataRef.current.action === 'login' && response.data?.token) {
            notifier.closeAll();
            access.login(response.data.token, {
              memberId: response.data.memberId || response.memberId,
              isLogged: true,
              fullMemberAccess: response.data.fullMemberAccess || response.active || false,
              viewCabinetMemberAccess: response.data.viewCabinetMemberAccess || false
            });
            dialog.close();
            if (sessionDataRef.current.switchAppOnLogin) {
              if (typeof sessionDataRef.current.onLogin === 'function') {
                sessionDataRef.current.onLogin();
              }
              switchApp(sessionDataRef.current.switchAppOnLogin);
            }
          } else if (sessionDataRef.current.action === 'change_password') {
            dialog.close();
            setTimeout(() => {
              notifier.push(text('dialog.session_key.notification__password_changed'));
            }, 100);
          }
        } else {
          processResponseError(response);
        }
      } catch (e) {
        setErrorWithReset(text('dialog.session_key.error_network'));
        if (storage) {
          if (!hasValidResendLimitError()) {
            storage.set('error', {
              error: text('dialog.session_key.error_network'),
              type: 'network'
            });
          }
        }
      } finally {
        setLoading(false);
      }
    };

    const handleResendCode = async () => {
      const remainingResendTime = getRemainingTime();
      if ((remainingResendTime > 0 && isResendLimit) || resendTimer > 0) return;
      
      setError('');
      
      setResending(true);

      try {
        const response = await api.sessionKeyResend({
          payload: {
            memberId: sessionDataRef.current.memberId,
            action: sessionDataRef.current.action,
          }
        });

        if (response.sessionKeyTTL) {
          const endTimeSec = Math.floor(Date.now() / 1000) + response.sessionKeyTTL;
          setLongResendTimer(endTimeSec);
          persistState('longResendTimer', { endTime: endTimeSec });

          setIsResendLimit(false);
          setResendTimer(0);
        }

        setDisableSendingSessionKey(false);
        setShowBlockMessage(false);
        setAttemptsLeft(null);

        storage.remove('blockMessage');
        storage.remove('attemptsLeft');
        setError('');
        preserveCurrentErrorRef.current = false;
        if (emailForStorage) {
          try {
            const errorStorageKey = `error_${emailForStorage}`;
            localStorage.removeItem(errorStorageKey);
          } catch (e) {
            console.error('Error force removing error:', e);
          }
        }

        setResendTimer(30);

      } catch (error) {
        processResponseError({
          result: 'error', 
          message: error.message
        });
      } finally {
        setResending(false);
      }
    };

    const checkAndAutoFillFromClipboard = useCallback(async () => {
      if (!document.hasFocus()) {
        return;
      }

      try {
        const permissionStatus = await navigator.permissions.query({ name: 'clipboard-read' });
        if (permissionStatus.state === 'denied') {
          return;
        }

        if (navigator.clipboard && navigator.clipboard.readText) {
          const clipboardText = await navigator.clipboard.readText();
          const sanitized = clipboardText ? clipboardText.replace(/\s/g, '') : '';

          if (!/^\d{6}$/.test(sanitized)) {
            return;
          }

          clearErrorIfAllowed();

          const digits = sanitized.split('');
          setCode(prevCode => {
            const newCode = Array(6).fill('').map((_, i) => digits[i] || '');
            return newCode;
          });

          const lastFilledIndex = Math.min(digits.length - 1, 5);
          setTimeout(() => {
            const inputElement = inputRefs.current[lastFilledIndex] || document.getElementById(`code-input-${lastFilledIndex}`);
            if (inputElement) {
              inputElement.focus();
              if (inputElement.setSelectionRange) {
                inputElement.setSelectionRange(1, 1);
              }
            }
          }, 0);
        }
      } catch (error) {
        if (error.name !== 'NotAllowedError') {
          console.error('Clipboard error:', error);
        }
      }
    }, [clearErrorIfAllowed, inputRefs]);

    useEffect(() => {
      if (!dialog.param) {
        return;
      }

      try {
        const decodedString = atob(dialog.param);
        const parsedData = JSON.parse(decodedString);

        sessionDataRef.current = parsedData;
        setResendTimer(30);

        if (parsedData.email) {
          setEmailFromParam(parsedData.email);
        }

        if (parsedData.action) {
          setSessionAction(parsedData.action);
        } else {
          setSessionAction(null);
        }

        if (parsedData.urlError) {
          processResponseError({
            result: 'error',
            message: parsedData.urlError,
            urlError: true
          });
        }
      } catch (e) {
        notifier.push(text('dialog.session_key.error_invalid_params'));
        if (sessionDataRef.current?.action === 'change_password') {
          setError('');
          if (storage) {
            storage.remove('error');
          }
        }
        dialog.close();
      }
    }, [dialog.param]);

    useEffect(() => {
      const remainingTime = getRemainingTime();
      if (remainingTime <= 0) {
        setDisableSendingSessionKey(false);
        return;
      }
    }, [longResendTimer, getRemainingTime]);

    useEffect(() => {
      if (!isResendLimit) return;

      const remainingTime = getRemainingTime();
      if (remainingTime <= 0) {
        setError('');
        setIsResendLimit(false);
        if (storage) {
          storage.remove('error');
        }
        return;
      }

      const remainingTimeMs = remainingTime * 1000;
      
      const timer = setTimeout(() => {
        setError('');
        setIsResendLimit(false);
        if (storage) {
          storage.remove('error');
        }
      }, remainingTimeMs);

      return () => clearTimeout(timer);
    }, [isResendLimit, longResendTimer, getRemainingTime, storage]);


    useEffect(() => {
      let mounted = true;

      const handleFocus = () => {
        if (mounted) {
          setTimeout(() => {
            checkAndAutoFillFromClipboard();
          }, 1000);
        }
      };

      if (document.hasFocus()) {
        checkAndAutoFillFromClipboard();
      }

      window.addEventListener('focus', handleFocus);

      return () => {
        notifier.closeAll();
        mounted = false;
        window.removeEventListener('focus', handleFocus);

        const email = sessionDataRef.current?.email;

        if (email) {
          const excludeKeys = ['longResendTimer', 'error'];
          storage.clearAppKeys(excludeKeys, email);
        } else {
          storage.clearAppKeys();
        }

        if (sessionDataRef.current.action === 'signup' && !signupCompletedRef.current) {

          if (email) {
            const continuationData = {
              email: email,
              action: sessionDataRef.current.action,
              memberId: sessionDataRef.current.memberId,
              utm_source: sessionDataRef.current.utm_source || '',
              partnerId: sessionDataRef.current.partnerId || '',
              timestamp: Date.now()
            };

            const storageKey = `continuationNotification_${email}`;
            localStorage.setItem(storageKey, JSON.stringify(continuationData));
          }
          const continueButtonText = text('dialog.signup.continue');

          notifier.push(text('dialog.signup.continuation_message'), {
            handler: () => {
              notifier.closeAll();
              const dialogParams = btoa(JSON.stringify({
                email: email,
                action: sessionDataRef.current.action,
                memberId: sessionDataRef.current.memberId,
                isFirstLaunch: false,
                utm_source: sessionDataRef.current.utm_source || '',
                partnerId: sessionDataRef.current.partnerId || ''
              }));
              dialog.open('SessionKeyDialog', dialogParams);
            },
            buttonText: continueButtonText,
            timeless: true
          });
        }
      };
    }, []);

    useEffect(() => {
      if (storage) {
        const errorData = storage.get('error');
        
        if (!preserveCurrentErrorRef.current) {
          if (errorData && errorData.type === 'resend_limit') {
            setIsResendLimit(true);
            setError(errorData.error);
            if (errorData.expiresAt && longResendTimer !== errorData.expiresAt) {
              setLongResendTimer(errorData.expiresAt);
            }
          } else if (errorData && errorData.type !== 'resend_limit') {
            setError(errorData.error);
          }
        } else if (errorData && errorData.type === 'resend_limit') {
          setIsResendLimit(true);
          if (errorData.expiresAt && longResendTimer !== errorData.expiresAt) {
            setLongResendTimer(errorData.expiresAt);
          }
        }

        const blockMessageData = storage.get('blockMessage');
        if (blockMessageData) {
          setShowBlockMessage(blockMessageData.show);
          setDisableSendingSessionKey(blockMessageData.show);
        }

        const attemptsLeftData = storage.get('attemptsLeft');
        if (attemptsLeftData) {
          setAttemptsLeft(attemptsLeftData.attempts);
        }
      }
    }, [storage, longResendTimer]);

    const sessionTitleKey = sessionAction === 'login'
      ? 'dialog.session_key.title_login'
      : 'dialog.session_key.title';

    return (
      <div className={targetDialogStyles.dialog}>
        <h3 className={dialogStyles.dialogTitle}>
          <Text>{sessionTitleKey}</Text>
        </h3>

        <p className={dialogStyles.description}>
          <Text>dialog.session_key.description_1</Text> <br/>
          <span className={selfStyles.registeredEmail}>{sessionDataRef.current?.email || ''}</span>
        </p>

        <CodeInputs
          code={code}
          error={error}
          loading={loading}
          resending={resending}
          disableSendingSessionKey={disableSendingSessionKey}
          onCodeChange={handleCodeChange}
          onInputFocus={handleInputFocus}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onInputBlur={handleInputBlur}
          isHovering={isHovering}
          isFocused={isFocused}
          inputRefs={inputRefs}
          styles={selfStyles}
          onClearError={clearErrorIfAllowed}
          onFocusChange={setIsFocused}
        />

        {(error) && (
          <div className={selfStyles.error}>
            {error}
          </div>
        )}

        <div className={selfStyles.actions}>
          <div className={selfStyles.buttonContainer}>
            <button
              className={clsx(buttonStyles.primaryBtn, buttonStyles.primaryBtn__medium, {
                [buttonStyles.primaryBtn__disabled]:
                  loading ||
                  code.join('').length !== 6 ||
                  disableSendingSessionKey,
              })}
              onClick={handleSubmit}
              disabled={
                loading ||
                code.join('').length !== 6 ||
                disableSendingSessionKey
              }
            >
              {loading ? (
                <Text>dialog.session_key.verifying</Text>
              ) : (
                <Text>dialog.session_key.verify_button</Text>
              )}
            </button>
          </div>
          <div className={selfStyles.buttonContainer}>
            <button
              className={clsx(
                buttonStyles.secondaryBtn,
                buttonStyles.secondaryBtn__medium,
              )}
              onClick={handleResendCode}
              disabled={resending || (displayRemainingTime > 0 && isResendLimit) || resendTimer > 0}
            >
              {displayRemainingTime > 0 && isResendLimit ? (
                <>
                  <Text>dialog.session_key.send_again_button</Text>
                  <span className={selfStyles.resendTimer}>&nbsp;({formatTime(displayRemainingTime)})</span>
                </>
              ) : resending ? (
                <Text>dialog.session_key.sending</Text>
              ) : resendTimer > 0 ? (
                <>
                  <Text>dialog.session_key.send_again_button</Text>
                  &nbsp;({resendTimer} сек.)
                </>
              ) : (
                <Text>dialog.session_key.send_again_button</Text>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  },
);
